import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconBuildingStore,
  IconEye,
  IconMovie,
} from '@tabler/icons-react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMingo } from '../context/MingoContext';
import { getHubBusinesses } from '../api/hubCategoryApi';
import { searchProductsPostsJobs, searchAll, searchCatalogItems } from '../api/searchApi';
import { getRegionLabel } from '../constants/regionLabels';
import {
  buildHubSearchPath,
  getHubCategoryLabel,
  normalizeHubCategoryId,
  normalizeSearchTab,
  resolveHubCategoryFromPath,
} from '../lib/hubCategoryRoute';
import { fetchPopularSearchQueries, recordSearchQueryServer } from '../api/searchHistoryApi';
import LoadingSpinner from '../components/LoadingSpinner';
import { getServiceOptionLabel } from '../lib/serviceOptions';
import { formatListingTime } from '../lib/formatListingTime';
import { fetchSearchTopAds } from '../api/searchTopAdsApi';
import { formatCurrencyAmount } from '../constants/cityMaster';
import { recordAdClick, recordAdImpression } from '../utils/adMatcher';
import { navigateBackToPrevious, withReturnState } from '../lib/goBack';

const RECENT_KEY = 'mingo_search_recent';
const MAX_RECENT = 10;

function escapeRe(s) {
  return String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function HighlightText({ text, query }) {
  const q = String(query || '').trim();
  const raw = String(text ?? '');
  if (!q) return raw;
  const parts = raw.split(new RegExp(`(${escapeRe(q)})`, 'gi'));
  return parts.map((part, i) => {
    if (part.toLowerCase() === q.toLowerCase()) {
      return (
        <mark key={i} className="mingo-search-hit">
          {part}
        </mark>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

/** 검색 상단 CPC 스폰서 결과 카드 — 노출 시 1회 impression 기록, 클릭 시 click 기록(자동 과금 트리거 연결) */
function SearchSponsoredItem({ ad, t, language, adStatsCtx, formatBizCategoryLabel }) {
  useEffect(() => {
    recordAdImpression({ id: ad.id, business_id: ad.businessId, title: ad.name, image: ad.image }, adStatsCtx);
  }, [ad, adStatsCtx]);

  return (
    <Link
      to={`/business/${ad.businessId}`}
      className="mingo-search-result-item"
      onClick={() =>
        recordAdClick({ id: ad.id, business_id: ad.businessId, title: ad.name, image: ad.image }, adStatsCtx)
      }
    >
      <div
        className="mingo-search-result-item__img"
        style={{
          backgroundImage: ad.image ? `url(${ad.image})` : 'none',
          backgroundColor: '#f3f4f6',
        }}
      />
      <div className="mingo-search-result-item__body">
        <p className="mingo-search-result-item__title">
          {ad.name}
          <span
            style={{
              marginLeft: 6,
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--mingo-text-secondary)',
              border: '1px solid var(--mingo-border, #ddd)',
              borderRadius: 4,
              padding: '1px 4px',
            }}
          >
            {t('search.adBadge')}
          </span>
        </p>
        <p className="mingo-search-result-item__meta">
          {formatBizCategoryLabel(ad.category)}
          {ad.region ? ` · ${getRegionLabel(ad.region, language)}` : ''}
          {Number(ad.rating) > 0 ? ` · ⭐ ${ad.rating}` : ''}
        </p>
      </div>
    </Link>
  );
}

function readRecent() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function pushRecent(term) {
  const t = String(term || '').trim();
  if (!t) return;
  const prev = readRecent().filter((x) => x.toLowerCase() !== t.toLowerCase());
  const next = [t, ...prev].slice(0, MAX_RECENT);
  window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
}

function SearchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const qParam = params.get('q') || params.get('query') || '';
  const categoryParam =
    normalizeHubCategoryId(params.get('category')) || resolveHubCategoryFromPath(location.pathname) || '';
  const tabFromUrl = normalizeSearchTab(params.get('tab')) || 'all';
  const { t, language, currentCountry, activeRegionView, selectedRegions } = useMingo();
  const hubLabel = useMemo(() => {
    const id = String(categoryParam || '').trim();
    if (!id) return '';
    const key = `business.cats.${id}`;
    const translated = t(key);
    if (translated !== key) return translated;
    return getHubCategoryLabel(id) || id;
  }, [categoryParam, t]);
  const isHubScoped = Boolean(categoryParam);
  const searchPlaceholder = useMemo(
    () => (hubLabel ? t('search.placeholderHub', { hub: hubLabel }) : t('search.placeholderAll')),
    [hubLabel, t],
  );
  const activeCountry = activeRegionView?.countryCode || currentCountry || 'VN';
  const cityCode = activeRegionView?.cityCode || null;
  const fallbackPopular = useMemo(() => [1, 2, 3, 4, 5].map((i) => t(`search.popularTerm${i}`)), [t]);
  const [query, setQuery] = useState(qParam);
  const [tab, setTab] = useState(tabFromUrl);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [products, setProducts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [bizResults, setBizResults] = useState([]);
  const [storyResults, setStoryResults] = useState([]);
  const [bizCategoryFilter, setBizCategoryFilter] = useState('');
  const [bizServiceFilter, setBizServiceFilter] = useState('');
  const [recentTick, setRecentTick] = useState(0);
  const [popular, setPopular] = useState(fallbackPopular);

  const SEARCH_TABS = useMemo(
    () =>
      [
        { key: 'all', label: t('search.tabAll') },
        { key: 'catalogItems', label: t('search.tabCatalogItems') },
        { key: 'products', label: t('search.tabProducts') },
        { key: 'stories', label: t('search.tabStories') },
        { key: 'posts', label: t('search.tabPosts') },
        { key: 'jobs', label: t('search.tabJobs') },
      ],
    [t],
  );

  useEffect(() => {
    void fetchPopularSearchQueries(12).then((rows) => {
      if (Array.isArray(rows) && rows.length > 0) setPopular(rows);
      else setPopular(fallbackPopular);
    });
  }, [fallbackPopular]);

  const recent = useMemo(() => {
    void recentTick;
    return readRecent();
  }, [recentTick]);

  const regionLabelRef = useRef(activeRegionView?.label || null);
  regionLabelRef.current = activeRegionView?.label || null;

  const runSearch = useCallback(async (raw, { showLoading = true } = {}) => {
    const term = String(raw || '').trim();
    setErr('');
    if (!term) {
      setProducts([]);
      setPosts([]);
      setJobs([]);
      setCatalogItems([]);
      setBizResults([]);
      setStoryResults([]);
      setLoading(false);
      return;
    }
    if (showLoading) setLoading(true);
    try {
      if (isHubScoped) {
        const businesses = await getHubBusinesses({
          category: categoryParam,
          search: term,
          countryCode: activeCountry || null,
          cityCode,
          limit: 40,
        });
        setProducts([]);
        setPosts([]);
        setJobs([]);
        setCatalogItems([]);
        setBizResults(businesses);
        setStoryResults([]);
      } else {
        const [{ products: pr, posts: po, jobs: jb }, allResults, catalogRows] = await Promise.all([
          searchProductsPostsJobs(term),
          searchAll(term, { region: regionLabelRef.current || null }),
          searchCatalogItems(term),
        ]);
        setProducts(pr);
        setPosts(po);
        setJobs(jb);
        setCatalogItems(catalogRows);
        setBizResults(allResults.businesses ?? []);
        setStoryResults(allResults.stories ?? []);
      }
      void recordSearchQueryServer(term);
    } catch (e) {
      setErr(String(e?.message || e || 'error'));
      setProducts([]);
      setPosts([]);
      setJobs([]);
      setCatalogItems([]);
      setBizResults([]);
      setStoryResults([]);
    } finally {
      setLoading(false);
    }
  }, [activeCountry, categoryParam, cityCode, isHubScoped]);

  useEffect(() => {
    setQuery(qParam);
    void runSearch(qParam, { showLoading: true });
  }, [qParam, runSearch]);

  useEffect(() => {
    if (isHubScoped) {
      setTab('all');
      return;
    }
    setTab(tabFromUrl);
  }, [categoryParam, isHubScoped, tabFromUrl]);

  const goWithSearchState = useCallback(
    (opts) => {
      navigate(buildHubSearchPath({ q: opts.q ?? qParam, category: categoryParam, tab: opts.tab ?? tab }), {
        replace: !!opts.replace,
        state: location.state,
      });
    },
    [categoryParam, location.state, navigate, qParam, tab],
  );

  // 검색 상단 CPC 스폰서 결과 — 통합 광고엔진(ad_campaigns, search_top) 기반, 조직 검색과 별개 fetch
  const [sponsoredBiz, setSponsoredBiz] = useState([]);
  useEffect(() => {
    let alive = true;
    const term = String(qParam || '').trim();
    if (!term) {
      setSponsoredBiz([]);
      return undefined;
    }
    fetchSearchTopAds(term, categoryParam || null, 3).then((rows) => {
      if (alive) setSponsoredBiz(Array.isArray(rows) ? rows : []);
    });
    return () => {
      alive = false;
    };
  }, [qParam, categoryParam]);
  const adStatsCtx = useMemo(
    () => ({ language, regions: selectedRegions, type: 'feed', category: 'search_top_ad' }),
    [language, selectedRegions],
  );

  const onSubmit = (e) => {
    e.preventDefault();
    const term = query.trim();
    if (term) pushRecent(term);
    setRecentTick((n) => n + 1);
    navigate(buildHubSearchPath({ q: term, category: categoryParam, tab }), {
      state: location.state,
    });
  };

  const empty = useMemo(
    () =>
      !loading &&
      qParam.trim() &&
      !products.length &&
      !posts.length &&
      !jobs.length &&
      !catalogItems.length &&
      !bizResults.length &&
      !storyResults.length,
    [loading, qParam, products.length, posts.length, jobs.length, catalogItems.length, bizResults.length, storyResults.length],
  );

  const showCatalogItems = tab === 'all' || tab === 'catalogItems';
  const showProducts = tab === 'all' || tab === 'products';
  const showPosts = tab === 'all' || tab === 'posts';
  const showJobs = tab === 'all' || tab === 'jobs';
  const showBusinesses = isHubScoped || tab === 'all' || tab === 'businesses';
  const showStories = tab === 'all' || tab === 'stories';
  const businessCategories = useMemo(() => {
    const set = new Set();
    for (const b of bizResults) {
      if (!b?.category) continue;
      set.add(String(b.category));
    }
    return [...set];
  }, [bizResults]);
  const businessServiceOptions = useMemo(() => {
    const set = new Set();
    for (const b of bizResults) {
      if (!Array.isArray(b.service_options)) continue;
      for (const opt of b.service_options) set.add(opt);
    }
    return [...set];
  }, [bizResults]);
  const formatBizCategoryLabel = useCallback(
    (category) => {
      const id = String(category || '').trim();
      if (!id) return '';
      const key = `business.cats.${id}`;
      const translated = t(key);
      if (translated !== key) return translated;
      return getHubCategoryLabel(id, language) || id;
    },
    [t, language],
  );
  const filteredBizResults = useMemo(() => {
    return bizResults.filter((b) => {
      const passCategory = !bizCategoryFilter || String(b?.category || '') === bizCategoryFilter;
      const passService = !bizServiceFilter || (Array.isArray(b?.service_options) && b.service_options.includes(bizServiceFilter));
      return passCategory && passService;
    });
  }, [bizResults, bizCategoryFilter, bizServiceFilter]);

  return (
    <div className="mingo-search-page">
      <div className="mingo-page-header mingo-page-header--layout-b">
          <button
            type="button"
            className="mingo-page-header__back"
            onClick={() => navigateBackToPrevious(navigate, location, { fallback: '/home' })}
            aria-label={t('common.back')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="mingo-page-header__title-wrap">
            <h1 className="mingo-page-header__title">{t('header.search')}</h1>
          </div>
          <div className="mingo-page-header__right-slot" />
        </div>
        <div className="mingo-search-page__top">
          <form className="mingo-search-page__form" onSubmit={onSubmit}>
            <input
              type="search"
              className="mingo-search-page__input"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
              aria-label={searchPlaceholder}
            />
            <button type="submit" className="mingo-search-page__submit">
              {t('header.search')}
            </button>
          </form>
        </div>

        {!qParam.trim() ? (
          <div className="mingo-search-page__extras">
            {recent.length ? (
              <section className="mingo-search-page__extra-block" aria-label={t('search.recentTitle')}>
                <h2 className="mingo-search-page__subh">{t('search.recentTitle')}</h2>
                <div className="mingo-search-chips">
                  {recent.map((w) => (
                    <button
                      key={w}
                      type="button"
                      className="mingo-search-chip"
                      onClick={() => {
                        setQuery(w);
                        pushRecent(w);
                        goWithSearchState({ q: w, replace: false });
                      }}
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
            <section className="mingo-search-page__extra-block" aria-label={t('search.popularTitle')}>
              <h2 className="mingo-search-page__subh">{t('search.popularTitle')}</h2>
              <div className="mingo-search-chips">
                {popular.map((w) => (
                  <button
                    key={w}
                    type="button"
                    className="mingo-search-chip mingo-search-chip--muted"
                    onClick={() => {
                      setQuery(w);
                      goWithSearchState({ q: w, replace: false });
                    }}
                  >
                    {w}
                  </button>
                ))}
              </div>
            </section>
          </div>
        ) : null}

        {qParam.trim() && !isHubScoped ? (
          <div className="mingo-search-tabs" role="tablist">
            {SEARCH_TABS.map((x) => (
              <button
                key={x.key}
                type="button"
                role="tab"
                aria-selected={tab === x.key}
                className={`mingo-search-tabs__btn${tab === x.key ? ' mingo-search-tabs__btn--on' : ''}`}
                onClick={() => {
                  setTab(x.key);
                  goWithSearchState({ tab: x.key, replace: true });
                }}
              >
                {x.label}
              </button>
            ))}
          </div>
        ) : null}

        {loading ? (
          <div className="mingo-home__loading" aria-busy="true">
            <LoadingSpinner label={t('home.loadingFeed')} />
          </div>
        ) : null}

        {err ? <p className="mingo-search-page__err">{err}</p> : null}

        {empty ? <p className="mingo-search-page__empty">{t('search.empty')}</p> : null}

        {!loading && showBusinesses && sponsoredBiz.length > 0 && (
          <section className="mingo-search-page__section" aria-label={t('search.adBadge')}>
            {sponsoredBiz.map((ad) => (
              <SearchSponsoredItem
                key={ad.id}
                ad={ad}
                t={t}
                language={language}
                adStatsCtx={adStatsCtx}
                formatBizCategoryLabel={formatBizCategoryLabel}
              />
            ))}
          </section>
        )}

        {!loading && showBusinesses && bizResults.length > 0 && (
          <section className="mingo-search-page__section" aria-label={t('search.tabBiz')}>
            {!isHubScoped && tab === 'all' ? (
              <h3 className="mingo-search-section__title">
                <IconBuildingStore size={16} stroke={2} aria-hidden style={{ verticalAlign: -2, marginRight: 4 }} />
                {t('search.tabBiz')}
              </h3>
            ) : null}
            {!isHubScoped && businessCategories.length ? (
              <div className="mingo-search-chips" style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  className={`mingo-search-chip${bizCategoryFilter ? '' : ' mingo-search-chip--muted'}`}
                  onClick={() => setBizCategoryFilter('')}
                >
                  {t('search.categoryAll')}
                </button>
                {businessCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={`mingo-search-chip${bizCategoryFilter === category ? ' mingo-search-chip--muted' : ''}`}
                    onClick={() => setBizCategoryFilter(category)}
                  >
                    {formatBizCategoryLabel(category)}
                  </button>
                ))}
              </div>
            ) : null}
            {businessServiceOptions.length ? (
              <div className="mingo-search-chips" style={{ marginBottom: 10 }}>
                <button
                  type="button"
                  className={`mingo-search-chip${bizServiceFilter ? '' : ' mingo-search-chip--muted'}`}
                  onClick={() => setBizServiceFilter('')}
                >
                  {t('search.tabAll')}
                </button>
                {businessServiceOptions.map((optId) => (
                  <button
                    key={optId}
                    type="button"
                    className={`mingo-search-chip${bizServiceFilter === optId ? ' mingo-search-chip--muted' : ''}`}
                    onClick={() => setBizServiceFilter(optId)}
                  >
                    {getServiceOptionLabel(optId, language)}
                  </button>
                ))}
              </div>
            ) : null}
            {filteredBizResults.map((biz) => (
              <Link
                key={biz.id}
                to={`/business/${biz.id}`}
                className="mingo-search-result-item"
              >
                <div
                  className="mingo-search-result-item__img"
                  style={{
                    backgroundImage: biz.images?.[0] ? `url(${biz.images[0]})` : 'none',
                    backgroundColor: '#f3f4f6',
                  }}
                />
                <div className="mingo-search-result-item__body">
                  <p className="mingo-search-result-item__title">
                    <HighlightText text={biz.name} query={qParam} />
                  </p>
                  <p className="mingo-search-result-item__meta">
                    {formatBizCategoryLabel(biz.category)}
                    {biz.region ? ` · ${getRegionLabel(biz.region, language)}` : ''}
                    {Number(biz.rating) > 0 ? ` · ⭐ ${biz.rating}` : ''}
                  </p>
                  {Array.isArray(biz.service_options) && biz.service_options.length > 0 ? (
                    <p className="mingo-search-result-item__meta" style={{ marginTop: 4 }}>
                      {biz.service_options.slice(0, 3).map((id) => getServiceOptionLabel(id, language)).join(' · ')}
                    </p>
                  ) : null}
                </div>
              </Link>
            ))}
            {!filteredBizResults.length && bizResults.length > 0 ? (
              <p className="mingo-search-page__empty">{t('search.empty')}</p>
            ) : null}
          </section>
        )}

        {!loading && showStories && storyResults.length > 0 && (
          <section className="mingo-search-page__section" aria-label={t('search.tabStories')}>
            {tab === 'all' ? (
              <h3 className="mingo-search-section__title">
                <IconMovie size={16} stroke={2} aria-hidden style={{ verticalAlign: -2, marginRight: 4 }} />
                {t('search.tabStories')}
              </h3>
            ) : null}
            <div className="mingo-search-stories-grid">
              {storyResults.map((story) => (
                <Link
                  key={story.id}
                  to={`/shorts?storyId=${encodeURIComponent(story.id)}`}
                  state={withReturnState(location)}
                  className="mingo-search-story-item"
                >
                  <div
                    className="mingo-search-story-item__thumb"
                    style={{
                      backgroundImage: story.thumb_url ? `url(${story.thumb_url})` : 'none',
                    }}
                  />
                  <p className="mingo-search-story-item__title">
                    <HighlightText text={story.title} query={qParam} />
                  </p>
                  <p className="mingo-search-story-item__meta">
                    {story.author_nickname ? `@${story.author_nickname} · ` : ''}
                    <><IconEye size={14} stroke={2} aria-hidden style={{ verticalAlign: -2, marginRight: 4 }} />{story.view_count ?? 0}</>
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {!loading && showCatalogItems && catalogItems.length > 0 && (
          <section className="mingo-search-page__section" aria-label={t('search.catalogItems')}>
            <h2 className="mingo-search-page__h">{t('search.catalogItems')}</h2>
            <ul className="mingo-search-page__list">
              {catalogItems.map((c) => (
                <li key={c.id}>
                  <Link to={`/catalog/${c.id}`} className="mingo-search-page__link">
                    <span className="mingo-search-page__title">
                      <HighlightText text={c.title} query={qParam} />
                    </span>
                    {c.short_description || c.description ? (
                      <span className="mingo-search-page__snippet">
                        <HighlightText text={String(c.short_description || c.description).slice(0, 120)} query={qParam} />
                      </span>
                    ) : null}
                    <span className="mingo-search-page__meta">
                      {c.base_price != null ? formatCurrencyAmount(c.base_price, c.currency_code) : ''}
                      {c.businesses?.name ? (
                        <>
                          <span className="mingo-search-page__dot"> · </span>
                          {c.businesses.name}
                        </>
                      ) : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!loading && showProducts && products.length > 0 && (
          <section className="mingo-search-page__section" aria-label={t('search.products')}>
            <h2 className="mingo-search-page__h">{t('search.products')}</h2>
            <ul className="mingo-search-page__list">
              {products.map((p) => (
                <li key={p.slug}>
                  <Link to={`/product/${p.slug}`} className="mingo-search-page__link">
                    <span className="mingo-search-page__title">
                      <HighlightText text={p.title} query={qParam} />
                    </span>
                    {p.description ? (
                      <span className="mingo-search-page__snippet">
                        <HighlightText text={String(p.description).slice(0, 120)} query={qParam} />
                      </span>
                    ) : null}
                    <span className="mingo-search-page__meta">
                      {p.price_display}
                      <span className="mingo-search-page__dot"> · </span>
                      {p.location}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!loading && showPosts && posts.length > 0 && (
          <section className="mingo-search-page__section" aria-label={t('search.posts')}>
            <h2 className="mingo-search-page__h">{t('search.posts')}</h2>
            <ul className="mingo-search-page__list">
              {posts.map((p) => (
                <li key={p.slug}>
                  <Link to={`/post/${p.slug}`} className="mingo-search-page__link">
                    <span className="mingo-search-page__title">
                      <HighlightText text={p.title} query={qParam} />
                    </span>
                    {p.preview ? (
                      <span className="mingo-search-page__snippet">
                        <HighlightText text={String(p.preview).slice(0, 120)} query={qParam} />
                      </span>
                    ) : null}
                    <span className="mingo-search-page__meta">
                      {p.location}
                      <span className="mingo-search-page__dot"> · </span>
                      {p.created_at || p.time_display
                        ? formatListingTime(p.created_at || p.time_display, t)
                        : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!loading && showJobs && jobs.length > 0 && (
          <section className="mingo-search-page__section" aria-label={t('search.jobs')}>
            <h2 className="mingo-search-page__h">{t('search.jobs')}</h2>
            <ul className="mingo-search-page__list">
              {jobs.map((j) => (
                <li key={j.id}>
                  <Link to="/jobs" className="mingo-search-page__link">
                    <span className="mingo-search-page__title">
                      <span className="mingo-search-page__job-type">
                        {j.type === 'seek' ? t('search.jobSeekBadge') : t('search.jobHireBadge')}
                      </span>{' '}
                      <HighlightText text={j.title} query={qParam} />
                    </span>
                    {j.description ? (
                      <span className="mingo-search-page__snippet">
                        <HighlightText text={String(j.description).slice(0, 120)} query={qParam} />
                      </span>
                    ) : null}
                    <span className="mingo-search-page__meta">
                      {j.salary}
                      <span className="mingo-search-page__dot"> · </span>
                      {j.region}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
    </div>
  );
}

export default SearchPage;
